/**
 * Prepared drops tab: creator + drafts/sent list.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ClipboardList, Layers, LayoutTemplate, X } from 'lucide-react';

import { Button } from '@/components/ui';
import { PresetsModal } from '@/features/smswiser-campaigns/components/PresetsModal';
import { getApiErrorMessage } from '@/lib/utils';
import { useToast } from '@/providers/ToastProvider';
import { useAuth } from '@/providers/AuthProvider';
import PrepareNewDropsForm from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import PreparedDropsDraftsList from '@/features/smswiser-campaigns/components/PreparedDropsDraftsList';
import { formatDateDMY } from '@/features/smswiser-campaigns/utils/dateFormat';
import { executeBulkUpdatePreparedDrops } from '@/features/smswiser-campaigns/utils/executeBulkUpdatePreparedDrops';
import { groupIdsByCountryKey } from '@/features/smswiser-campaigns/utils/preparedDropBulkByCountry';

import { ReinstateYesterdayButton } from './components/ReinstateYesterdayButton';
import { useSmswiserCampaignPreparedDropsTab } from './hooks/useSmswiserCampaignPreparedDropsTab';

export interface SmswiserCampaignPreparedDropsTabProps {
  countryKey: string;
  campaigns: Array<{ id: number; name: string }>;
  wiserId: number;
  workDate: string;
  campaignName: string;
  titlePrefix: string;
  defaultSubTab?: 'drafts' | 'sent';
  onSelectCampaign: (id: number) => void;
  highlightPreparedDropId?: string | null;
  onInvestigationFocusConsumed?: () => void;
}

export const SmswiserCampaignPreparedDropsTab: FC<SmswiserCampaignPreparedDropsTabProps> = (props) => {
  const { hasPermission } = useAuth();
  const canKillSentDrops = hasPermission('drops.kill');
  const {
    countryKey,
    campaigns,
    wiserId,
    workDate,
    campaignName,
    titlePrefix,
    defaultSubTab,
    highlightPreparedDropId,
    onInvestigationFocusConsumed,
    showPrepare,
    setShowPrepare,
    showPlusOne,
    country,
    perfTagsQuery,
    dropListTags,
    textPerfTagsQuery,
    gatewayPerfTagsQuery,
    prepared,
    creative,
    updateCreative,
    updatePreparedFlex,
    bulkUpdatePreparedFlex,
    deletePreparedFlex: _deletePreparedFlex,
    bulkDeletePreparedFlex,
    sendPreparedFlex,
    deleteCampaignText,
    deleteCampaignTextsBulk,
    routes,
    leadsLists,
    availableTags,
    tagsByTextId,
    tagsByGatewayId,
    tlds,
    drops,
    isBusy,
    actions,
    handleBulkSendComplete,
    handleAddSender,
    handleAddText,
    handleDeleteSender,
    handleDeleteSenders,
    handleDeleteText,
    handleDeleteTextsBulk,
    handleBulkSaveAndSend,
    handleSentSuccess,
    handleReorder,
    onSelectCampaign: _onSelectCampaign,
    formCampaignId,
    handleFormCampaignChange,
  } = useSmswiserCampaignPreparedDropsTab(props);

  const tagColors = useMemo(
    () => ({
      ...perfTagsQuery.data?.tagColors,
      ...textPerfTagsQuery.data?.tagColors,
      ...gatewayPerfTagsQuery.data?.tagColors,
    }),
    [
      perfTagsQuery.data?.tagColors,
      textPerfTagsQuery.data?.tagColors,
      gatewayPerfTagsQuery.data?.tagColors,
    ],
  );

  const emptyCreativeRef = useRef({ senderNames: [] as string[], smsTexts: [] as string[], rotators: null });
  const creativeForForm = creative.data ?? emptyCreativeRef.current;
  const creativeForList = creative.data ?? emptyCreativeRef.current;
  const [showPresetsModal, setShowPresetsModal] = useState(false);
  const [presetSelectedDrafts, setPresetSelectedDrafts] = useState<typeof drops>([]);

  const { error: toastError, loading: toastLoading, dismiss: toastDismiss } = useToast();
  const dateLabel = formatDateDMY(workDate);

  // Bring the creator into view on open so its sticky footer (Create button) is reachable
  // without scrolling past the page chrome above the inline panel.
  const prepareCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showPrepare) return;
    const id = requestAnimationFrame(() =>
      prepareCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
    return () => cancelAnimationFrame(id);
  }, [showPrepare]);

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto p-4 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-border">
      <div className="flex shrink-0 items-center justify-between gap-4 rounded-lg border border-border/40 bg-background px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
            <ClipboardList className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground">Prepared Drops</h2>
            <p className="text-xs text-muted-foreground">
              Managing campaigns for <span className="font-medium text-foreground">{dateLabel}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ReinstateYesterdayButton
            countryKey={countryKey}
            workDate={workDate}
            onReinstate={actions.handleReinstate}
            isBusy={isBusy}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPresetSelectedDrafts([]);
              setShowPresetsModal(true);
            }}
            className="gap-2 border-dashed"
          >
            <Layers className="h-4 w-4" />
            Add Presets
          </Button>
          {!showPrepare && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPrepare(true)}
              className="gap-2 border-dashed"
            >
              <LayoutTemplate className="h-4 w-4" />
              Open Creator
            </Button>
          )}
        </div>
      </div>

      {country.isError && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm font-medium text-destructive">
          Error loading country configuration. Please check your connection.
        </div>
      )}

      {showPrepare && (
        <div
          ref={prepareCardRef}
          className="relative flex shrink-0 scroll-mt-3 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm animate-in fade-in slide-in-from-top-4 duration-300"
        >
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border bg-muted/20 px-4 py-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <LayoutTemplate className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <h2 className="truncate text-sm font-semibold text-foreground">Draft Creator</h2>
                <p className="truncate text-xs text-muted-foreground">
                  Configure route, creative &amp; leads, then create drafts
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
              onClick={() => setShowPrepare(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          {/* max-h, not h: the creator takes the height its fields actually need
           *  and only scrolls once it runs out of viewport. Pinned at 80vh it
           *  scrolled even on a tall screen with room to spare. */}
          <div className="flex max-h-[min(92vh,1000px)] min-h-[460px] w-full flex-col overflow-hidden">
            <PrepareNewDropsForm
              countryKey={countryKey}
              campaigns={campaigns}
              wiserId={showPrepare ? formCampaignId : wiserId}
              workDate={workDate}
              titlePrefix={titlePrefix}
              campaignName={campaignName}
              gateways={routes}
              tlds={tlds}
              leadsLists={leadsLists}
              tagColors={tagColors}
              tagsByTextId={tagsByTextId}
              tagsByGatewayId={tagsByGatewayId}
              tagsLoading={perfTagsQuery.isLoading}
              tagFilterOptions={availableTags}
              creative={creativeForForm}
              isBusy={isBusy}
              onSelectCampaign={handleFormCampaignChange}
              onCreateDrafts={(payload) =>
                actions.handleCreateDrafts(payload, { wiserId, workDate })
              }
              onUpdateCreative={async (p) => {
                await updateCreative.mutateAsync(p);
              }}
              onDeleteSender={handleDeleteSender}
              onDeleteSenders={handleDeleteSenders}
              onDeleteText={async (smsText) => {
                await deleteCampaignText.mutateAsync(smsText);
              }}
              onDeleteTextsBulk={async (smsTexts) => {
                await deleteCampaignTextsBulk.mutateAsync(smsTexts);
              }}
            />
          </div>
        </div>
      )}

      <div className="flex min-h-[400px] flex-1 flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="flex min-h-0 flex-1 flex-col bg-muted/5">
          <PreparedDropsDraftsList
            countryKey={countryKey}
            campaigns={campaigns}
            workDate={workDate}
            defaultSubTab={defaultSubTab}
            highlightPreparedDropId={highlightPreparedDropId}
            onInvestigationFocusConsumed={onInvestigationFocusConsumed}
            drops={drops}
            isLoading={prepared.isLoading}
            gateways={routes}
            leadsLists={leadsLists}
            perfTagsQuery={perfTagsQuery}
            dropListTags={dropListTags}
            tagsByTextId={tagsByTextId}
            textPerformanceTagColors={tagColors}
            tlds={tlds}
            creative={creativeForList}
            isBusy={isBusy}
            sendingDropId={
              sendPreparedFlex.isPending && sendPreparedFlex.variables?.preparedDropId
                ? sendPreparedFlex.variables.preparedDropId
                : null
            }
            killingDropIds={actions.killingDropIds}
            onSend={(id) => {
              const drop = drops.find((d) => d.id === id);
              if (!drop) return;
              sendPreparedFlex.mutate(
                { countryKey: drop.countryKey, preparedDropId: id, wiserId: drop.wiserId },
                {
                  onSuccess: handleSentSuccess,
                  onError: (err) => {
                    toastError('Send failed', getApiErrorMessage(err, 'Could not send drop'));
                  },
                },
              );
            }}
            showPlusOne={showPlusOne}
            onUpdate={(id, payload) => {
              const drop = drops.find((d) => d.id === id);
              if (drop) {
                updatePreparedFlex.mutate({
                  countryKey: drop.countryKey,
                  id,
                  payload,
                  wiserId: drop.wiserId,
                });
              }
            }}
            onBulkUpdate={async (ids, payload, scheduleIntervalMinutes) => {
              const items = ids
                .map((id) => drops.find((d) => d.id === id))
                .filter((d): d is NonNullable<typeof d> => Boolean(d))
                .map((d) => ({ id: d.id, wiserId: d.wiserId }));
              if (items.length === 0) return;
              const count = items.length;
              const loadingId = toastLoading('Editing drafts...', `${count} draft${count !== 1 ? 's' : ''} — applying changes`);
              try {
                await executeBulkUpdatePreparedDrops({
                  ids,
                  drops,
                  payload,
                  scheduleIntervalMinutes,
                  mutateAsync: bulkUpdatePreparedFlex.mutateAsync.bind(bulkUpdatePreparedFlex),
                });
                toastDismiss(loadingId);
              } catch (err) {
                toastDismiss(loadingId);
                toastError('Bulk edit failed', getApiErrorMessage(err, 'Could not save changes'));
              }
            }}
            onDelete={async (ids) => {
              try {
                const byCountry = groupIdsByCountryKey(ids, drops);
                // One id for the whole click, so a delete spanning countries
                // undoes as a single step rather than only its last request.
                const undoGroupId = crypto.randomUUID();
                for (const [ck, groupIds] of byCountry) {
                  await bulkDeletePreparedFlex.mutateAsync({
                    countryKey: ck,
                    ids: groupIds,
                    undoGroupId,
                  });
                }
              } catch (err) {
                toastError('Delete failed', getApiErrorMessage(err, 'Could not delete drops'));
                throw err;
              }
            }}
            onBulkSend={actions.handleBulkSend}
            onCreateSmswiserDraft={actions.handleCreateSmswiserDraft}
            onBulkSaveAndSend={handleBulkSaveAndSend}
            onDuplicateDraft={actions.handleDuplicateDraft}
            onKillSent={canKillSentDrops ? actions.handleKillSent : undefined}
            onCopyAsDraft={actions.handleCopyAsDraft}
            onBulkSendComplete={handleBulkSendComplete}
            onBeforeEditSent={actions.canEditSentDrop}
            onSaveSentEdit={actions.handleSaveSentEdit}
            onAddSender={handleAddSender}
            onAddText={handleAddText}
            onDeleteSender={handleDeleteSender}
            onDeleteText={handleDeleteText}
            onDeleteTextsBulk={handleDeleteTextsBulk}
            onReorder={handleReorder}
            onSaveAsPreset={(selected) => {
              setPresetSelectedDrafts(selected);
              setShowPresetsModal(true);
            }}
          />
        </div>
      </div>

      {showPresetsModal && (
        <PresetsModal
          countryKey={countryKey}
          workDate={workDate}
          catalog={{
            campaigns,
            gateways: routes,
            leadsLists: leadsLists.map((l) => ({ id: l.id, name: l.name })),
            tlds,
          }}
          campaignWiserId={wiserId}
          selectedDrafts={presetSelectedDrafts}
          onClose={() => {
            setShowPresetsModal(false);
            setPresetSelectedDrafts([]);
          }}
          onApplied={() => prepared.refetch()}
        />
      )}
    </div>
  );
};

export default SmswiserCampaignPreparedDropsTab;
